import React, { useEffect, useRef } from 'react';
import { useDesignStore } from '../../stores/designStore';
import { DesignerToolbar } from './DesignerToolbar';
import { PreviewPanel } from '../preview/PreviewPanel';
import { VisualAIInput } from './VisualAIInput';
import { VersionHistory } from './VersionHistory';
import { SuggestionsPanel } from './SuggestionsPanel';

export const DesignerContainer: React.FC = () => {
  const { 
    currentDesign, 
    previewHtml,
    currentDevice,
    versions,
    addVersion,
  } = useDesignStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 设计变更防抖处理 - 自动保存版本
  useEffect(() => {
    if (!currentDesign || !previewHtml) return;

    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 设置新的防抖定时器
    debounceTimerRef.current = setTimeout(() => {
      // 自动保存版本（如果与上一个版本有差异）
      const lastVersion = versions[versions.length - 1];
      const shouldSaveVersion = !lastVersion || 
        lastVersion.previewHtml !== previewHtml ||
        JSON.stringify(lastVersion.design) !== JSON.stringify(currentDesign);

      if (shouldSaveVersion) {
        addVersion({
          design: currentDesign,
          previewHtml,
          note: '自动保存',
        });
      }
    }, 5000); // 5秒防抖

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [currentDesign, previewHtml, versions, addVersion]);

  // 获取设备宽度
  const getDeviceWidth = () => {
    switch (currentDevice) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      case 'desktop':
      default:
        return '100%';
    }
  };

  return (
    <div 
      ref={containerRef}
      className="h-full flex flex-col bg-gray-50"
    >
      {/* 顶部工具栏 */}
      <DesignerToolbar />

      {/* 主内容区 - 65%宽度 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 左侧：预览区域 */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* 预览面板 */}
          <div className="flex-1 overflow-hidden bg-gray-100">
            <div 
              className="h-full mx-auto transition-all duration-300"
              style={{ 
                width: getDeviceWidth(),
                maxWidth: '100%',
              }}
            >
              <PreviewPanel />
            </div>
          </div>

          {/* 底部：可视化AI输入区 */}
          <div className="h-auto min-h-[140px] max-h-[200px] bg-white border-t">
            <VisualAIInput />
          </div>
        </div>

        {/* 右侧：版本历史 + 建议面板 */}
        <div className="w-72 bg-white border-l flex flex-col overflow-hidden">
          {/* 调整建议面板 */}
          <div className="flex-1 overflow-hidden">
            <SuggestionsPanel />
          </div>

          {/* 版本历史 */}
          <div className="h-1/2 border-t overflow-hidden">
            <VersionHistory />
          </div>
        </div>
      </div>
    </div>
  );
};
